'use client'

import Link from 'next/link'
import type { LucideIcon } from 'lucide-react'
import { Tooltip, TooltipContent, TooltipTrigger } from '@elmnts/ui/tooltip'
import { usePermissions } from '@/hooks/usePermissions'
import type { PermissionExpr } from '@/lib/permissions/check'
import { cn } from '@/lib/utils'

export interface RailItemProps {
  icon: LucideIcon
  label: string
  href?: string
  onClick?: () => void
  require?: PermissionExpr
  disabled?: boolean
  disabledHint?: string
  active?: boolean
  className?: string
}

export function RailItem({ icon: Icon, label, href, onClick, require: expr, disabled, disabledHint, active, className }: RailItemProps) {
  const { hasPermission } = usePermissions()
  if (expr && !hasPermission(expr)) return null

  const body = (
    <span
      data-active={active || undefined}
      className={cn(
        'flex size-9 items-center justify-center rounded-md text-sidebar-foreground/70 transition-colors',
        'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
        'data-[active=true]:bg-sidebar-accent data-[active=true]:text-sidebar-accent-foreground',
        disabled && 'pointer-events-none opacity-40',
      )}
    >
      <Icon className="size-4" />
    </span>
  )

  const trigger = disabled ? (
    <span tabIndex={0} aria-disabled="true" className={cn('inline-flex focus:outline-none', className)}>
      <button type="button" aria-label={label} disabled tabIndex={-1} className="pointer-events-none">
        {body}
      </button>
    </span>
  ) : !href ? (
    <button type="button" aria-label={label} onClick={onClick} className={className}>
      {body}
    </button>
  ) : (
    <Link href={href} aria-label={label} className={className}>
      {body}
    </Link>
  )

  return (
    <Tooltip>
      <TooltipTrigger asChild>{trigger}</TooltipTrigger>
      <TooltipContent side="right">{disabled && disabledHint ? disabledHint : label}</TooltipContent>
    </Tooltip>
  )
}
